const MAX_IMAGE_DIMENSION = 1600;
const WEBP_QUALITY = 0.82;
const REFRESH_PREVIEW_MAX_DIMENSION = 1200;
const REFRESH_PREVIEW_QUALITY = 0.72;
const IMAGE_DECODE_TIMEOUT_MS = 12000;
const VIDEO_PREVIEW_LOAD_TIMEOUT_MS = 7000;

function getPlaceMediaHost(url = "") {
  try {
    return new URL(String(url || "")).hostname.toLowerCase();
  } catch {
    return "";
  }
}

function buildWebpName(file = {}) {
  const originalName = String(file?.name || "").trim();
  const baseName = originalName.replace(/\.[^.]+$/, "") || `image_${Date.now()}`;
  return `${baseName}.webp`;
}

function buildPosterName(value = "") {
  const rawName = String(value || "").trim();
  const baseName = rawName.replace(/\.[^.]+$/, "").replace(/[^a-z0-9_-]+/gi, "-") || `video-${Date.now()}`;
  return `${baseName}-poster.webp`;
}

function buildCanvasForImage(image, maxDimension) {
  const naturalWidth = Number(image.naturalWidth || image.width || 0);
  const naturalHeight = Number(image.naturalHeight || image.height || 0);

  if (!naturalWidth || !naturalHeight) return null;

  const scale = Math.min(1, maxDimension / Math.max(naturalWidth, naturalHeight));
  const targetWidth = Math.max(1, Math.round(naturalWidth * scale));
  const targetHeight = Math.max(1, Math.round(naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
  return canvas;
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    let settled = false;
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        // ignore
      }
    };

    const resolveOnce = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(image);
    };

    const rejectOnce = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    };

    image.onload = () => {
      resolveOnce();
    };

    image.onerror = () => {
      rejectOnce(new Error("Failed to decode image"));
    };

    timeoutId = setTimeout(() => {
      rejectOnce(new Error("Timed out while decoding image"));
    }, IMAGE_DECODE_TIMEOUT_MS);

    image.src = objectUrl;
  });
}

function loadVideoFromFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    let settled = false;
    let timeoutId = null;

    const cleanup = () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        // ignore
      }
      video.pause?.();
      video.removeAttribute("src");
      video.load();
    };

    const resolveOnce = () => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      resolve({ video, cleanup });
    };

    const rejectOnce = (err) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      cleanup();
      reject(err);
    };

    video.onloadeddata = () => {
      resolveOnce();
    };

    video.onloadedmetadata = () => {
      if (video.readyState >= 2) {
        resolveOnce();
        return;
      }
      try {
        const duration = Number(video.duration || 0);
        video.currentTime = duration > 0.2 ? Math.min(0.2, duration - 0.05) : 0.1;
      } catch {
        // ignore seek issues and wait for loadeddata or timeout
      }
    };

    video.onseeked = () => {
      if (video.readyState >= 2) {
        resolveOnce();
      }
    };

    video.onerror = () => {
      rejectOnce(new Error("Failed to decode video"));
    };

    timeoutId = setTimeout(() => {
      rejectOnce(new Error("Timed out while decoding video"));
    }, VIDEO_PREVIEW_LOAD_TIMEOUT_MS);

    video.src = objectUrl;
    video.load();
  });
}

function canvasToWebpBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error("Failed to encode image"));
      },
      "image/webp",
      WEBP_QUALITY
    );
  });
}

async function optimizeImageFile(file) {
  if (!(file instanceof File)) return file;

  const mimeType = String(file.type || "").toLowerCase();
  if (!mimeType.startsWith("image/")) return file;
  if (mimeType === "image/gif") return file;

  const image = await loadImageFromFile(file);
  const canvas = buildCanvasForImage(image, MAX_IMAGE_DIMENSION);
  if (!canvas) return file;

  const blob = await canvasToWebpBlob(canvas);
  return new File([blob], buildWebpName(file), {
    type: "image/webp",
    lastModified: Date.now(),
  });
}

export async function buildSessionPreviewDataUrl(file) {
  if (!(file instanceof File)) return "";

  const mimeType = String(file.type || "").toLowerCase();
  if (!mimeType.startsWith("image/")) return "";
  if (mimeType === "image/gif") return "";

  const image = await loadImageFromFile(file);
  const canvas = buildCanvasForImage(image, REFRESH_PREVIEW_MAX_DIMENSION);
  if (!canvas) return "";

  try {
    return canvas.toDataURL("image/webp", REFRESH_PREVIEW_QUALITY) || "";
  } catch {
    return "";
  }
}

export async function buildVideoSessionPreviewDataUrl(file) {
  if (!(file instanceof File)) return "";

  const mimeType = String(file.type || "").toLowerCase();
  if (!mimeType.startsWith("video/")) return "";

  let loaded = null;
  try {
    loaded = await loadVideoFromFile(file);
    const { video, cleanup } = loaded;
    const canvas = buildCanvasForImage(video, REFRESH_PREVIEW_MAX_DIMENSION);
    cleanup();
    loaded = null;
    if (!canvas) return "";
    return canvas.toDataURL("image/webp", REFRESH_PREVIEW_QUALITY) || "";
  } catch {
    if (loaded?.cleanup) loaded.cleanup();
    return "";
  }
}

function dataUrlToFile(dataUrl, fileName = "", fallbackType = "image/webp") {
  const raw = String(dataUrl || "");
  if (!raw.startsWith("data:")) return null;

  const commaIndex = raw.indexOf(",");
  if (commaIndex < 0) return null;

  const header = raw.slice(0, commaIndex);
  const payload = raw.slice(commaIndex + 1);
  const mimeMatch = header.match(/^data:([^;]+);base64$/i);
  const mimeType = mimeMatch?.[1] || fallbackType;

  try {
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], fileName || `image_${Date.now()}.webp`, {
      type: mimeType,
      lastModified: Date.now(),
    });
  } catch {
    return null;
  }
}

export function buildVideoPosterFileFromPreview(dataUrl = "", sourceName = "") {
  return dataUrlToFile(dataUrl, buildPosterName(sourceName), "image/webp");
}

export function isGooglePlaceMediaUrl(url = "") {
  const host = getPlaceMediaHost(url);
  return (
    host.includes("googleusercontent.com") ||
    host.includes("googleapis.com") ||
    host.includes("gstatic.com")
  );
}

export function isGooglePlaceImportUrlItem(item) {
  if (!item || item.file) return false;
  const source = String(item?.source || "").trim().toLowerCase();
  const url = String(item?.url || "").trim();
  if (!url) return false;
  return source === "place" && isGooglePlaceMediaUrl(url);
}

export function isForwardedSourceMediaItem(item) {
  if (!item || item.file) return false;
  const url = String(item?.url || "").trim();
  return Boolean(item?.forwardedSourceMedia === true && url);
}

export function shouldUploadWizardMediaItem(item) {
  if (!item) return false;
  return Boolean(item.file) || isGooglePlaceImportUrlItem(item) || isForwardedSourceMediaItem(item);
}

export async function buildUploadableMediaFile(item) {
  if (!shouldUploadWizardMediaItem(item)) return null;
  const file = item?.file || null;
  if (!file && isForwardedSourceMediaItem(item)) {
    throw new Error("Forwarded source media must be copied server-side before upload.");
  }
  if (!file) return null;

  try {
    return await optimizeImageFile(file);
  } catch {
    return file;
  }
}

export function mapStoredMediaToWizardItem(media = {}) {
  const rawType = String(media?.type || "").toLowerCase();
  const isVideo = rawType === "video" || rawType.startsWith("video/");
  const url = String(media?.url || "");
  const sessionPreviewUrl = String(media?.sessionPreviewUrl || "").trim();
  const thumbnailUrl = String(media?.thumbnailUrl || "").trim();
  const posterUrl = String(media?.posterUrl || "").trim();
  const posterStoragePath = String(media?.posterStoragePath || "").trim();
  const isEmbeddedImage = !isVideo && url.startsWith("data:");
  const normalizedType = isVideo
    ? "video/mp4"
    : (rawType.startsWith("image/") ? media.type : "image/jpeg");
  const restoredFile = isEmbeddedImage
    ? dataUrlToFile(url, String(media?.fileName || "").trim(), normalizedType)
    : null;

  return {
    file: restoredFile,
    url,
    type: normalizedType,
    source: media?.source || "",
    storagePath: media?.storagePath || "",
    draftOwnedMedia: media?.draftOwnedMedia === true,
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(posterUrl ? { posterUrl } : {}),
    ...(posterStoragePath ? { posterStoragePath } : {}),
    ...(sessionPreviewUrl ? { sessionPreviewUrl } : {}),
  };
}
